import { useEffect, useId, useRef, useState } from 'react';

import { Icon } from '@/components/ui/icon';
import { CONTROL_BASE, CONTROL_SIZE } from '@/components/ui/input';
import { Popover } from '@/components/ui/popover';
import { useClickOutside, useEscapeKey } from '@/hooks';
import { cn } from '@/lib/cn';

export interface ComboboxOption {
  value: string;
  label: string;
  /** A second line under the label - what it is, where it came from. */
  hint?: string;
  disabled?: boolean;
}

export interface ComboboxProps {
  options: ComboboxOption[];
  value: string | null;
  onChange: (value: string) => void;
  placeholder?: string;
  invalid?: boolean;
  disabled?: boolean;
  id?: string;
  name?: string;
  className?: string;
  'aria-describedby'?: string;
  'aria-label'?: string;
  /** Shows a clear affordance once a value is set. */
  clearable?: boolean;
  /** Wording for when nothing matches what was typed. */
  emptyMessage?: string;
  /**
   * Told what is being typed, for a list that comes from the server.
   *
   * The control stays in charge of its own input either way; this only
   * reports. Pair it with `filter={false}`, or the options that came back for
   * the query get filtered against it a second time and a server that matched
   * on a code or an email hides its own answer.
   */
  onQueryChange?: (query: string) => void;
  /**
   * Whether to narrow the options against what is typed. On by default.
   *
   * Off is for a list already narrowed by whoever supplied it.
   */
  filter?: boolean;
  /** Shows a quiet note instead of `emptyMessage` while matches are on the way. */
  loading?: boolean;
}

/**
 * A list you type into.
 *
 * `Select` is right for a list you can hold in your head - a status, a kind, a
 * sort order. It is wrong for two hundred and forty-four countries: its
 * typeahead needs you to know the first letters of the exact label, and
 * anything else means scrolling. This one filters as you type, on a substring,
 * so "emir" finds United Arab Emirates and "ind" finds both India and
 * Indonesia.
 *
 * The keyboard contract is the same as Select's, because they look the same
 * and nobody should have to notice which one they are on: Up/Down move, Home
 * and End jump, Enter commits, Escape closes without committing.
 */
export function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Search…',
  invalid = false,
  disabled = false,
  id,
  name,
  className,
  'aria-describedby': describedBy,
  'aria-label': ariaLabel,
  clearable = false,
  emptyMessage = 'Nothing matches',
  onQueryChange,
  filter = true,
  loading = false,
}: ComboboxProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;
  const listboxId = `${controlId}-listbox`;

  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);

  const selected = options.find((option) => option.value === value) ?? null;

  /* Closed, the field shows what is chosen; open, it shows what is being
     typed - so opening does not wipe the current answer off the screen. */
  const text = open ? query : (selected?.label ?? '');

  const matches = filter ? matching(options, open ? query : '') : options;

  function close(): void {
    setOpen(false);
    setQuery('');
    setActiveIndex(0);
    /* Cleared on the way out too, so reopening does not answer with the last
       search's matches while the new request is still going. */
    onQueryChange?.('');
  }

  function type(next: string): void {
    setQuery(next);
    setActiveIndex(0);
    onQueryChange?.(next);
  }

  useClickOutside([inputRef, listRef], close, open);
  useEscapeKey(() => {
    close();
    inputRef.current?.focus();
  }, open);

  /* Keeps the active option in view while arrowing through a long list. */
  useEffect(() => {
    if (!open) {
      return;
    }

    listRef.current
      ?.querySelector<HTMLElement>(`[data-index="${String(activeIndex)}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  function commit(index: number): void {
    const option = matches[index];

    if (!option || option.disabled) {
      return;
    }

    onChange(option.value);
    close();
    inputRef.current?.focus();
  }

  function move(step: number): void {
    if (!open) {
      setOpen(true);

      return;
    }

    setActiveIndex((current) => {
      const next = current + step;

      if (next < 0) {
        return matches.length - 1;
      }

      return next >= matches.length ? 0 : next;
    });
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>): void {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        move(1);
        break;
      case 'ArrowUp':
        event.preventDefault();
        move(-1);
        break;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActiveIndex(0);
        }
        break;
      case 'End':
        if (open) {
          event.preventDefault();
          setActiveIndex(matches.length - 1);
        }
        break;
      case 'Enter':
        if (open) {
          event.preventDefault();
          commit(activeIndex);
        }
        break;
      case 'Tab':
        /* Leaving the field settles on what is highlighted, the way a
           spreadsheet does - it is what the eye was already on. */
        if (open && matches.length > 0) {
          commit(activeIndex);
        }
        break;
      default:
        break;
    }
  }

  return (
    <>
      {/* Mirrors the value for uncontrolled form reads and autofill. */}
      {name && <input type="hidden" name={name} value={value ?? ''} />}

      {/* `w-full` to match Select, whose trigger has always had it. Without it
          this collapsed to the input's intrinsic width inside a flex toolbar,
          so swapping a Select for a Combobox silently narrowed the control -
          which is exactly the difference nobody should have to notice between
          two things that look the same. A caller's own `sm:w-56` still wins
          above that breakpoint, because className is merged last. */}
      <div className={cn('relative w-full', className)}>
        <input
          ref={inputRef}
          id={controlId}
          role="combobox"
          type="text"
          autoComplete="off"
          spellCheck={false}
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-autocomplete="list"
          aria-activedescendant={open ? `${listboxId}-${String(activeIndex)}` : undefined}
          aria-invalid={invalid ? true : undefined}
          aria-describedby={describedBy}
          aria-label={ariaLabel}
          disabled={disabled}
          placeholder={selected ? selected.label : placeholder}
          value={text}
          onChange={(event) => {
            type(event.target.value);
            setOpen(true);
          }}
          onFocus={() => {
            setOpen(true);
          }}
          onClick={() => {
            /* Focus alone is not enough: after Escape the field is closed but
               still focused, and clicking it again must bring the list back. */
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          className={cn(
            CONTROL_BASE,
            CONTROL_SIZE,
            'pr-12',
            invalid ? 'border-danger' : 'border-border-strong hover:border-content-subtle',
          )}
        />

        <span className="pointer-events-none absolute inset-y-0 right-2 flex items-center gap-1">
          {clearable && selected && !disabled && (
            /* A real button, in the tab order. It used to be a `role="button"`
               span at `tabIndex={-1}`, which meant the only way to empty a
               nationality or an assignee was the mouse. The name carries the
               value because a job form puts four of these on one screen, and
               four buttons all called "Clear selection" tell a screen-reader
               user nothing about which field they are on. */
            <button
              type="button"
              aria-label={`Clear ${selected.label}`}
              className="pointer-events-auto rounded-sm text-content-subtle hover:text-content"
              onClick={() => {
                onChange('');
                close();
                inputRef.current?.focus();
              }}
            >
              <Icon name="close" size={12} />
            </button>
          )}

          <Icon
            name="chevron-down"
            size={14}
            className={cn('text-content-subtle transition-transform', open && 'rotate-180')}
          />
        </span>
      </div>

      <Popover anchorRef={inputRef} open={open} matchAnchorWidth className="py-1">
        <div ref={listRef} id={listboxId} role="listbox" className="max-h-60 overflow-y-auto">
          {matches.length === 0 && (
            <p className="px-2 py-2 text-xs text-content-subtle">
              {/* "Nothing matches" while the answer is still in flight reads
                  as a result, and somebody stops typing a name that is there. */}
              {loading ? 'Searching…' : emptyMessage}
            </p>
          )}

          {matches.map((option, index) => {
            const isSelected = option.value === value;
            const isActive = index === activeIndex;

            return (
              <div
                key={option.value}
                id={`${listboxId}-${String(index)}`}
                role="option"
                data-index={index}
                aria-selected={isSelected}
                aria-disabled={option.disabled}
                onMouseDown={(event) => {
                  /* Before the blur, or the field closes under the pointer. */
                  event.preventDefault();
                }}
                onClick={() => {
                  commit(index);
                }}
                onMouseEnter={() => {
                  if (!option.disabled) {
                    setActiveIndex(index);
                  }
                }}
                className={cn(
                  'flex cursor-pointer items-center justify-between gap-2 px-2 py-1.5 text-xs',
                  option.disabled && 'cursor-not-allowed text-content-subtle',
                  isActive && !option.disabled && 'bg-surface-muted',
                  isSelected && 'font-medium text-primary',
                )}
              >
                <span className="min-w-0">
                  <span className="block truncate">{option.label}</span>
                  {option.hint && (
                    <span className="block truncate text-2xs text-content-subtle">
                      {option.hint}
                    </span>
                  )}
                </span>
                {isSelected && <Icon name="check" size={14} className="shrink-0" />}
              </div>
            );
          })}
        </div>
      </Popover>
    </>
  );
}

/**
 * What the typed text matches.
 *
 * Substring rather than prefix, because people type the distinctive part of a
 * name: "emirates" for United Arab Emirates, "korea" for both Koreas. Matches
 * that start with the text come first, so exact-ish beginnings still win.
 */
function matching(options: ComboboxOption[], query: string): ComboboxOption[] {
  const term = query.trim().toLowerCase();

  if (term === '') {
    return options;
  }

  const hits = options.filter(
    (option) =>
      option.label.toLowerCase().includes(term) || option.value.toLowerCase().includes(term),
  );

  return hits.sort((a, b) => {
    const rank = (option: ComboboxOption): number =>
      Number(!option.label.toLowerCase().startsWith(term));

    return rank(a) - rank(b);
  });
}
